import { useEffect, useState } from "react";
import { Button, Drawer, Heading, HeadingRow, FormManager, FormCheckboxInput, FormNumberInput, FormTextAreaInput, FormTextInput } from "cai-fusion";
import { useModels } from "../../../contexts/ModelsContext";

import { ReactComponent as IconCloseSquare } from "../../../../../images/close-square.svg";

const ModelConfigurationDrawer = ({isHidden, onClose}) => {
    // const [isEditing, setIsEditing] = useState(false);
    // The model used for this form. This is updated when the model selected gets updated.
    const [formModel, setFormModel] = useState({});
    const [formModelParams, setFormModelParams] = useState([]);

    const { createModel, updateModel, currModel, currModelParams, isEditing, isLoading, loadModels } = useModels();

    useEffect(() => {
        loadModels(true);
    }, [])

    useEffect(() => {
        setFormModel({...currModel})
    }, [currModel])

    useEffect(() => {
        setFormModelParams([...currModelParams]);
    }, [currModelParams])

    const onFormChange = (fieldId, value) => {
        setFormModel({...formModel, [fieldId]: value });
    }

    const onParamsChange = (modelParameterId, fieldId, value) => {
        setFormModelParams(formModelParams.map((param) => 
            param.modelParameterId === modelParameterId ? {...param, [fieldId]: Number(value)} : param
        ));
    }

    const handleSubmit = async () => {
        if (isEditing) {
            updateModel(formModel.engine, {...formModel}, formModelParams);
        } else {
            createModel({...formModel}, formModelParams);
        }
    }

    return (
        <Drawer
            clickOutsideToClose
            isHidden={isHidden}
            onClose={onClose}
            header={
                <HeadingRow
                    title="Model Configuration"
                    eyebrow="Models"
                    size={3}
                >
                    <div className="m-icon-row m-icon-row--right-align">
                        <a
                            id="drawerCloseBtn"
                            className="a-icon m-icon-row__icon"
                            title="Close Associate Details"
                            onClick={onClose}
                        >
                            <IconCloseSquare className="a-icon-link__icon">
                                {" "}
                            </IconCloseSquare>
                        </a>
                    </div>
                </HeadingRow>
            }
            actions={<>
                <Button
                    children="Cancel"
                    outline
                    onClick={onClose}
                />
                <Button
                    children={`${isEditing ? "Update" : "Create"} Model`}
                    onClick={() => {
                        handleSubmit();
                        onClose();
                    }}
                />
            </>}
        >
            {/* 
                - Startup Notes 
                - Output Token Limit
            */}
            <FormManager formObj={formModel} onInputChange={onFormChange} isInRows>
                <div className="row l-mb-xs l-mt-sm">
                    <div className="col-12">
                        <Heading size={3}>Basic Information</Heading>
                    </div>
                </div>
                <FormTextInput
                    displayName="Engine"
                    fieldId="engine"
                    placeholder="Enter engine config"
                    disabled={isEditing}
                />
                <FormTextInput 
                    displayName="Model Name"
                    fieldId="displayName" 
                    placeholder="Enter model name"
                />
                <FormTextAreaInput 
                    displayName="Description"
                    fieldId="description"
                    placeholder="Description of the model"
                />

                <div className="row l-mt-sm l-mb-xs">
                    <div className="col-12">
                        <Heading size={3}>Deployment Configuration</Heading>
                    </div>
                </div>
                <FormTextInput 
                    displayName="Deployment Name"
                    fieldId="deploymentName"
                    placeholder="Set deployment name for API calls"
                />
                <FormTextInput 
                    displayName="Deployment Location"
                    fieldId="deploymentLocation"
                    placeholder="Set deployment location"
                />
                <FormTextAreaInput 
                    displayName="Startup Notes"
                    fieldId="startupNotes"
                    placeholder="Enter startup notes of the model"
                />

                <div className="row l-mt-sm l-mb-xs">
                    <div className="col-12">
                        <Heading size={3}>API Configuration</Heading>
                    </div>
                </div>
                <FormTextInput 
                    displayName="Model API Endpoint"
                    fieldId="modelApiEndpoint"
                    placeholder="URL endpoint for this model"
                />
                <FormTextInput 
                    displayName="Model API Key"
                    fieldId="modelApiKey"
                    placeholder="API key for accessing this model"
                />
                <FormTextInput 
                    displayName="Model API Version"
                    fieldId="modelApiVersion"
                    placeholder="Version of the model"
                />

                <div className="row l-mt-sm l-mb-xs">
                    <div className="col-12">
                        <Heading size={3}>Model Limits & Costs</Heading>
                    </div>
                </div>
                <FormTextInput 
                    displayName="Token Limit"
                    fieldId="tokenLimit"
                    placeholder="Enter token limit"
                />
                <FormTextInput 
                    displayName="Output Token Limit"
                    fieldId="outputTokenLimit"
                    placeholder="Enter output token limit"
                />
                <FormTextInput 
                    displayName="Input Cost Per 1k"
                    fieldId="inputCostPerUnit"
                    placeholder="Enter input cost per 1k"
                />
                <FormTextInput 
                    displayName="Output Cost Per 1k"
                    fieldId="outputCostPerUnit"
                    placeholder="Enter output cost per 1k"
                />

                <div className="row l-mt-sm l-mb-xs">
                    <div className="col-12">
                        <Heading size={3}>Model Capabilities</Heading>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <FormManager formObj={formModel} onInputChange={onFormChange}>
                            <FormCheckboxInput 
                                displayName="Is Enabled"
                                fieldId="isEnabled"
                            />
                            <FormCheckboxInput 
                                displayName="Is Text Model"
                                fieldId="isTextModel"
                            />
                            <FormCheckboxInput 
                                displayName="Is Image Model"
                                fieldId="IsImageModel"
                            />
                            <FormCheckboxInput 
                                displayName="Is Vision Model"
                                fieldId="isVisionModel"
                            />
                            <FormCheckboxInput 
                                displayName="Is Default Text Model"
                                fieldId="isDefaultTextModel"
                            />
                            <FormCheckboxInput 
                                displayName="Is Default Image Model"
                                fieldId="isDefaultImageModel"
                            />
                            <FormCheckboxInput 
                                displayName="Is Default Vision Model"
                                fieldId="isDefaultVisionModel"
                            />
                        </FormManager>
                    </div>
                </div>

                { (formModelParams?.length > 0 || isLoading) &&
                    <div className="row l-mt-sm l-mb-xs">
                        <div className="col-12">
                            <Heading size={3}>Model Parameters</Heading>
                        </div>
                    </div>
                }
                { formModelParams?.length === 0 && isLoading &&
                    <div className="row">
                        <div className="col-12">
                            <p>Loading model parameters, please wait...</p>
                        </div>
                    </div>
                }
                { formModelParams.map((param) => {
                    return (
                        <FormModelParameter 
                            key={param?.modelParameterId}
                            param={param}
                            onChange={(fieldId, value) => {
                                onParamsChange(param?.modelParameterId, fieldId, value);
                            }}
                        />
                    );
                })}

                <div className="row l-mt-sm l-mb-xs">
                    <div className="col-12">
                        <Heading size={3}>Advanced Configuration</Heading>
                    </div>
                </div>
                <FormTextInput 
                    displayName="Encoding Engine"
                    fieldId="encodingEngine"
                    placeholder="Enter encoding engine name"
                />

            </FormManager>
        </Drawer>
    );
}

const FormModelParameter = ({param, onChange}) => {
    return (
        <div className="row">
            <div className="col-12">
                <div className="m-form-group">
                    <Heading size={4}>{param?.name ?? ""}</Heading>
                    <div className="row">
                        <div className="col-4">
                            <FormNumberInput 
                                displayName="Minimum Value"
                                step={0.1}
                                fieldId="minValue"
                                formValue={param?.minValue ?? 0}
                                onInputChange={(fieldId, value) => onChange(fieldId, value)}
                            />
                        </div>
                        <div className="col-4">
                            <FormNumberInput 
                                displayName="Maximum Value"
                                step={0.1}
                                fieldId="maxValue"
                                formValue={param?.maxValue ?? 0}
                                onInputChange={(fieldId, value) => onChange(fieldId, value)}
                            />
                        </div>
                        <div className="col-4">
                            <FormNumberInput 
                                displayName="Default Value"
                                step={0.1}
                                fieldId="defaultValue"
                                formValue={param?.defaultValue ?? 0}
                                onInputChange={(fieldId, value) => onChange(fieldId, value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ModelConfigurationDrawer;